import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../styles.css'; // Ensure styles are properly linked

const API_URL = window.location.protocol === 'https:'
  ? 'https://www.siriusmusicgroup.com/api/s3/songs'
  : 'http://52.25.19.40:3030/api/s3/songs'; // Use HTTP only for local development

const artistsData = [
  { name: 'lionkush', displayName: 'LionKush' },
  { name: 'smuggla', displayName: 'Smuggla' },
  { name: 'wyse', displayName: 'Wyse' },
  { name: 'frisky', displayName: 'Frisky' }
];

const Music = () => {
  const [songs, setSongs] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentSongIndex, setCurrentSongIndex] = useState(null); // Track the currently playing song index
  const [isPlaying, setIsPlaying] = useState(false); // Track play/pause state
  const [isShuffling, setIsShuffling] = useState(false); // Track shuffle state
  const [isRepeating, setIsRepeating] = useState(false); // Track repeat state
  const [wakeLock, setWakeLock] = useState(null); // Track wake lock state
  const audioRef = useRef(null);

  // Fetch all songs for all artists
  useEffect(() => {
    fetchAllSongs();
  }, []);

  // Request Wake Lock to prevent screen from locking
  useEffect(() => {
    const requestWakeLock = async () => {
      if ('wakeLock' in navigator && isPlaying) {
        try {
          const wakeLockInstance = await navigator.wakeLock.request('screen');
          setWakeLock(wakeLockInstance);
          wakeLockInstance.addEventListener('release', () => {
            console.log('Wake Lock was released');
          });
        } catch (err) {
          console.error(`${err.name}, ${err.message}`);
        }
      }
    };

    if (isPlaying) {
      requestWakeLock();
    }

    return () => {
      if (wakeLock) {
        wakeLock.release().then(() => {
          setWakeLock(null);
        });
      }
    };
  }, [isPlaying]);

  // Preload the next song for seamless transitions
  useEffect(() => {
    const preloadNextSong = () => {
      const allSongsFlat = Object.values(songs).flat();
      if (!allSongsFlat || allSongsFlat.length === 0) return;

      let nextIndex;
      if (isShuffling) {
        nextIndex = Math.floor(Math.random() * allSongsFlat.length);
      } else {
        nextIndex = (currentSongIndex + 1) % allSongsFlat.length;
      }

      const nextSongUrl = allSongsFlat[nextIndex]?.url;
      if (nextSongUrl) {
        const audio = new Audio();
        audio.src = nextSongUrl;
        audio.preload = 'auto'; // Preload the next song
      }
    };

    if (isPlaying && currentSongIndex !== null) {
      preloadNextSong();
    }
  }, [currentSongIndex, isPlaying, isShuffling, songs]);

  // Fetch all songs for all artists
  const fetchAllSongs = async () => {
    setLoading(true);
    let allSongs = {};
    for (let artist of artistsData) {
      try {
        const response = await axios.get(`${API_URL}/${artist.name}`);
        allSongs[artist.name] = response.data.songs;
      } catch (error) {
        console.error(`Error fetching songs for ${artist.name}:`, error);
      }
    }
    setSongs(allSongs);
    setLoading(false);
  };

  // Play a specific song by index
  const playSong = (index) => {
    const allSongsFlat = Object.values(songs).flat();
    if (!allSongsFlat || allSongsFlat.length === 0) return;

    setCurrentSongIndex(index);
    if (audioRef.current) {
      audioRef.current.src = allSongsFlat[index]?.url; // Update the audio source
      audioRef.current.load(); // Reload the audio element
      setTimeout(async () => {
        try {
          await audioRef.current.play(); // Start playing the new song
          setIsPlaying(true);
        } catch (error) {
          console.error('Playback failed:', error);
          setIsPlaying(false); // Ensure state reflects the failure
        }
      }, 500); // Small delay to ensure the song is loaded
    }
  };

  // Toggle play/pause
  const togglePlayPause = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Playback failed:', error);
      setIsPlaying(false); // Ensure state reflects the failure
    }
  };

  // Play the next song automatically
  const playNextSong = () => {
    const allSongsFlat = Object.values(songs).flat();
    if (!allSongsFlat || allSongsFlat.length === 0) return;

    let nextIndex;
    if (isShuffling) {
      nextIndex = Math.floor(Math.random() * allSongsFlat.length);
    } else {
      nextIndex = (currentSongIndex + 1) % allSongsFlat.length;
    }
    playSong(nextIndex);
  };

  // Play the previous song
  const playPreviousSong = () => {
    const allSongsFlat = Object.values(songs).flat();
    if (!allSongsFlat || allSongsFlat.length === 0) return;

    let prevIndex;
    if (isShuffling) {
      prevIndex = Math.floor(Math.random() * allSongsFlat.length);
    } else {
      prevIndex = currentSongIndex === 0 ? allSongsFlat.length - 1 : currentSongIndex - 1;
    }
    playSong(prevIndex);
  };

  // Handle song ended event
  const handleSongEnded = () => {
    if (isRepeating) {
      audioRef.current.play(); // Repeat the same song
    } else {
      playNextSong(); // Play the next song
    }
  };

  // Toggle shuffle mode
  const toggleShuffle = () => {
    setIsShuffling(!isShuffling);
  };

  // Toggle repeat mode
  const toggleRepeat = () => {
    setIsRepeating(!isRepeating);
  };

  return (
    <div className="container mt-5">
      <h2 className="text-warning text-center">🎵 Music Library</h2>
      <p className="text-center">Explore the latest music from all our artists.</p>

      {/* Now Playing Indicator */}
      <p className="text-center now-playing">
        Now Playing:{' '}
        {currentSongIndex !== null
          ? Object.values(songs).flat()[currentSongIndex]?.name
          : 'No song playing'}
      </p>

      {/* Custom Audio Player */}
      <div className="custom-audio-player d-flex align-items-center justify-content-center">
        <button
          className="btn btn-warning m-2"
          onClick={playPreviousSong}
        >
          Previous
        </button>
        <button
          className={`btn btn-${isPlaying ? 'danger' : 'success'} m-2`}
          onClick={togglePlayPause}
        >
          {isPlaying ? 'Pause' : 'Play'}
        </button>
        <button
          className="btn btn-warning m-2"
          onClick={playNextSong}
        >
          Next
        </button>
        <button
          className={`btn ${isShuffling ? 'btn-success' : 'btn-secondary'} m-2`}
          onClick={toggleShuffle}
        >
          Shuffle: {isShuffling ? 'On' : 'Off'}
        </button>
        <button
          className={`btn ${isRepeating ? 'btn-success' : 'btn-secondary'} m-2`}
          onClick={toggleRepeat}
        >
          Repeat: {isRepeating ? 'On' : 'Off'}
        </button>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onEnded={handleSongEnded} // Automatically play the next song or repeat
        playsInline
        webkit-playsinline
        preload="metadata"
      >
        <source
          src={
            currentSongIndex !== null
              ? Object.values(songs).flat()[currentSongIndex]?.url
              : ''
          }
          type="audio/mp3"
        />
        Your browser does not support the audio element.
      </audio>

      {/* Songs for All Artists */}
      {loading ? (
        <p className="text-center">Loading music...</p>
      ) : (
        <>
          {artistsData.map((artist) => (
            <div key={artist.name} className="mt-4">
              <h3 className="text-warning">{artist.displayName}</h3>
              <ul className="list-group">
                {songs[artist.name]?.length > 0 ? (
                  songs[artist.name].map((song, index) => {
                    const allSongsFlat = Object.values(songs).flat();
                    const songIndex = allSongsFlat.findIndex((s) => s.url === song.url);
                    return (
                      <li
                        key={index}
                        className={`list-group-item playlist-item d-flex justify-content-between align-items-center ${
                          currentSongIndex === songIndex ? 'active' : ''
                        }`}
                        onClick={() => playSong(songIndex)} // Play the clicked song
                        style={{ cursor: 'pointer' }}
                      >
                        <span>{song.name}</span>
                        {currentSongIndex === songIndex && (
                          <span className="badge bg-warning text-dark">Playing</span>
                        )}
                      </li>
                    );
                  })
                ) : (
                  <p className="text-center">No songs available for {artist.displayName}.</p>
                )}
              </ul>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default Music;